import React, { useMemo } from "react";
import { Card } from "primereact/card";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import type { ClosureState, ReportingOptions } from "../../../types/stationPerformance";
import { useDashboardSummary } from "../hooks/useDashboardSummary";
import type { MonthSummary } from "../hooks/useDashboardSummary";
import { useUnitReconciliation } from "../hooks/useUnitReconciliation";
import ProjectedValue from "./ProjectedValue";
import { COCHARGE_REVENUE_TEXTS as TEXTS, NOT_APPLICABLE_REASONS, cochargeFeeExplanation, cochargeFeeLabel, formatDays } from "../StationPerformance.consts";
import { berlinSamePointLabel, MIN_DAYS_COVERED } from "../utils/projection";

interface PerformanceDashboardProps {
  options: ReportingOptions;
  onSelectUnit: (unitId: string) => void;
  /** Analysed month (`YYYY-MM`), chosen on the page. */
  month: string;
  /** Injectable for tests; the projections count days on the Berlin calendar of this moment. */
  now?: Date;
}

const closureSeverity = (closure: ClosureState) =>
  closure === "SEALED" ? "success" : closure === "GRACE" ? "info" : "warning";

const euro = (cents: number | null | undefined) =>
  cents == null
    ? "—"
    : (cents / 100).toLocaleString(undefined, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      });

/** Whole cents: the cocharge share parts must visibly add up (€15.00 + €100.00 + €82.50 = €197.50). */
const euroCents = (cents: number | null | undefined) =>
  cents == null
    ? "—"
    : (cents / 100).toLocaleString(undefined, {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

/** "2026-08" → "August 2026". Read by people, so the month gets its name; pass a locale to pin the language. */
const monthName = (yearMonth: string, locale?: string) => {
  const [y, m] = yearMonth.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: "long", year: "numeric" });
};

const Metric: React.FC<{
  label: string;
  value: React.ReactNode;
  caption?: string;
  tone?: "neutral" | "good" | "bad";
  testId?: string;
  children?: React.ReactNode;
}> = ({ label, value, caption, tone = "neutral", testId, children }) => (
  <Card className="h-full" data-testid={testId}>
    <div className="text-color-secondary text-sm mb-2">{label}</div>
    <div
      className="text-4xl font-bold mb-1"
      style={{
        color: tone === "good" ? "var(--green-600)" : tone === "bad" ? "var(--red-600)" : undefined,
      }}
    >
      {value}
    </div>
    {caption && <div className="text-color-secondary text-sm">{caption}</div>}
    {children}
  </Card>
);

/**
 * A THG figure. Null has two meanings that must not be confused: FREE_ONLY (not applicable: a dash
 * with the existing reason on hover, as in MeasureValueCell) and a year without a price ("not configured").
 */
const ThgAmount: React.FC<{
  cents: number | null | undefined;
  notApplicable?: boolean;
  neverComputed?: boolean;
}> = ({ cents, notApplicable, neverComputed }) =>
  neverComputed ? (
    <span className="text-color-secondary">—</span>
  ) : notApplicable ? (
    <span className="text-color-secondary" title={NOT_APPLICABLE_REASONS.FREE_ONLY_SEGMENT}>
      —
    </span>
  ) : cents == null ? (
    <span className="text-color-secondary">{TEXTS.notConfigured}</span>
  ) : (
    <>{euroCents(cents)}</>
  );

/** One part of the cocharge share: both months' amounts, then its explanation in small muted text. */
const SharePart: React.FC<{
  label: string;
  explanation: string;
  current: number | null | undefined;
  previous: number | null | undefined;
  currentLabel: string;
  previousLabel: string;
  /** Month-end projection of the current amount; absent unless the month is open. */
  currentProjection?: React.ReactNode;
  thg?: {
    currentNotApplicable: boolean;
    previousNotApplicable: boolean;
    currentNeverComputed: boolean;
    previousNeverComputed: boolean;
  };
}> = ({ label, explanation, current, previous, currentLabel, previousLabel, currentProjection, thg }) => (
  <div className="mt-3 pt-3 border-top-1 surface-border" data-testid={`share-part-${label}`}>
    <div className="font-bold mb-1">{label}</div>
    <div className="flex justify-content-between align-items-baseline">
      <span className="text-color-secondary text-sm">{currentLabel}</span>
      <span className="font-bold">
        {thg ? <ThgAmount
            cents={current}
            notApplicable={thg.currentNotApplicable}
            neverComputed={thg.currentNeverComputed}
          /> : euroCents(current)}
      </span>
    </div>
    {currentProjection}
    <div className="flex justify-content-between align-items-baseline">
      <span className="text-color-secondary text-sm">{previousLabel}</span>
      <span>
        {thg ? <ThgAmount
            cents={previous}
            notApplicable={thg.previousNotApplicable}
            neverComputed={thg.previousNeverComputed}
          /> : euroCents(previous)}
      </span>
    </div>
    <div className="text-color-secondary text-sm mt-1">{explanation}</div>
  </div>
);

/** Total THG / THG payout: this month vs last month in the same layout as the other tiles. */
const ThgTile: React.FC<{
  testId: string;
  label: string;
  explanation: string;
  current: MonthSummary | null;
  previous: MonthSummary | null;
  pick: (m: MonthSummary) => number | null;
  currentLabel: string;
  previousLabel: string;
  /** Caption under the headline figure: "{month} so far" while the month runs, "Whole month" once finished. */
  currentCaption: string;
  /** Builds the projection line for the current amount, compared with last month's amount. */
  projection?: (value: number | null, previousValue: number | null) => React.ReactNode;
}> = ({ testId, label, explanation, current, previous, pick, currentLabel, previousLabel, currentCaption, projection }) => {
  const cur = current && !current.neverComputed ? pick(current) : null;
  const prev = previous && !previous.neverComputed ? pick(previous) : null;
  const notComputed = (m: MonthSummary | null) => !m || m.neverComputed;
  return (
    <Metric
      testId={testId}
      label={label}
      value={notComputed(current) ? "—" : <ThgAmount cents={cur} notApplicable={current?.thgNotApplicable} />}
      caption={currentCaption}
    >
      {projection?.(current?.thgNotApplicable ? null : cur, previous?.thgNotApplicable ? null : prev)}
      <div className="text-color-secondary text-sm mt-1">{explanation}</div>
      <div className="mt-3 pt-3 border-top-1 surface-border">
        <div className="flex justify-content-between align-items-baseline">
          <span className="text-color-secondary text-sm">{previousLabel}</span>
          <span className="text-xl font-bold">
            {notComputed(previous) ? (
              "—"
            ) : (
              <ThgAmount cents={prev} notApplicable={previous?.thgNotApplicable} />
            )}
          </span>
        </div>
      </div>
    </Metric>
  );
};

/** A station card. Clicking through opens that station in Station detail. */
const TopStation: React.FC<{
  label: string;
  summary: MonthSummary | null;
  caption: string;
  onSelectUnit: (unitId: string) => void;
}> = ({ label, summary, caption, onSelectUnit }) => {
  const unit = summary?.topUnit ?? null;
  return (
    <Card className="h-full">
      <div className="text-color-secondary text-sm mb-2">{label}</div>
      {unit ? (
        <>
          <button
            type="button"
            className="p-link text-2xl font-bold mb-1 text-left"
            style={{ color: "var(--primary-color)" }}
            onClick={() => onSelectUnit(unit.unitId)}
            title="Open this station in Station detail"
          >
            {unit.stationName}
          </button>
          <div className="text-xl mb-1">{euro(unit.moneyCents)}</div>
          <div className="text-color-secondary text-sm">
            {unit.sessions.toLocaleString()} sessions · {caption}
          </div>
        </>
      ) : (
        <>
          <div className="text-2xl font-bold mb-1 text-color-secondary">—</div>
          <div className="text-color-secondary text-sm">
            {summary?.neverComputed ? "This month has not been calculated yet" : "No stations earned anything"}
          </div>
        </>
      )}
    </Card>
  );
};

/**
 * The at-a-glance view: is the money going up, whose share is it, and which station is carrying it.
 *
 * Analyses the month chosen on the page (the running month by default) and is deliberately small.
 * Everything that needs a column heading to make sense lives in the Station list tab instead: that one is for people
 * who came to read numbers, this one is for people who came to ask one question.
 *
 * For the running month the comparison is pace-adjusted (past months compare whole months). A running month measured against a whole finished month always looks
 * like a collapse, which is precisely the misreading this feature exists to prevent, so the bar compares
 * against last month scaled to the same number of days and says outright that the scaling is an estimate.
 */
const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({ month, options, onSelectUnit, now }) => {
  // Forced to TOTAL: this view reports the whole amount taken and cocharge's share as two separate
  // cards, so reading it on the share basis would make the first card silently mean the second.
  const totalBasis = useMemo(
    () => ({ ...options, moneyView: "TOTAL" as const }),
    [options],
  );
  const { summary, loading, error } = useDashboardSummary(totalBasis, month, now);
  // Both denominators for the month just finished. Reuses the panel's hook rather than deriving a
  // second per-unit figure here, so the dashboard and the reconciliation panel cannot disagree.
  const { reconciliation } = useUnitReconciliation({
    month: summary.previousMissing ? null : summary.previousMonth,
    options: totalBasis,
  });

  if (loading) {
    return (
      <div className="flex justify-content-center p-5">
        <ProgressSpinner style={{ width: 48, height: 48 }} />
      </div>
    );
  }

  if (error) {
    return <Message severity="error" text={error} className="w-full" />;
  }

  const {
    current,
    previous,
    isCurrentMonth,
    previousMissing,
    previousClosure,
    daysCovered,
    asOf,
    daysInCurrentMonth,
    previousPaceCents,
    paceDeltaPercent,
    thgMissingYears,
  } = summary;

  const currentLabel = current ? monthName(current.month) : "This month";
  const previousLabel = previous ? monthName(previous.month) : "Last month";
  // A running month is "so far"; a finished one is simply whole.
  const elapsedCaption = isCurrentMonth
    ? `${formatDays(daysCovered)} of ${daysInCurrentMonth} days so far`
    : "Whole month";
  const soFar = isCurrentMonth ? " so far" : "";

  // A never-computed month has no money figure at all (unknown, not zero): "—" and no bar.
  const previousMoney = previous?.neverComputed ? null : previous?.moneyCents;
  const currentMoney = current?.neverComputed ? null : current?.moneyCents;
  // Running month: last month's bar is the whole month, with a dashed line and the amount at the point it
  // had got to by now (BOF-84 / BOF-85). Without a pace figure (previous month missing, or too early on
  // the 1st) there is no line.
  const samePoint = isCurrentMonth && previousPaceCents != null;
  const samePointLabel = berlinSamePointLabel(asOf, Boolean(current?.liveDayIncluded));
  const tooEarly = isCurrentMonth && previous != null && !previousMissing && daysCovered < MIN_DAYS_COVERED;
  // Both bars and the dashed line are scaled against the same maximum, otherwise the lengths cannot be
  // compared by eye. The same point can pass last month's whole total when this month is longer (31 days
  // after 30, March after February), so it is part of the maximum too.
  const barMax = Math.max(currentMoney ?? 0, previousMoney ?? 0, samePoint ? previousPaceCents : 0, 1);
  const width = (cents: number) => `${Math.max(0, Math.min(100, (cents / barMax) * 100))}%`;

  // A projection line for one additive figure of the current month. It renders nothing unless the month
  // is OPEN, and nothing for a missing value ("—", not configured, not applicable).
  // `previousValue` is last month's actual for the same figure, so the line can show ↑/↓ vs last month.
  const projected = (
    value: number | null | undefined,
    format: (v: number) => string,
    previousValue?: number | null,
  ) =>
    current && !current.neverComputed ? (
      <ProjectedValue
        value={value}
        yearMonth={current.month}
        closure={current.closure}
        liveDayIncluded={current.liveDayIncluded}
        format={format}
        now={asOf}
        previousValue={previous && !previous.neverComputed ? previousValue : null}
        previousLabel={previousLabel}
      />
    ) : null;
  const previousThg = (value: number | null | undefined) => (previous?.thgNotApplicable ? null : value);
  const thgPart = (value: number | null | undefined) => (current?.thgNotApplicable ? null : value);

  const partial = Boolean(current?.partial || previous?.partial);

  return (
    <div className="flex flex-column gap-3">
      {partial && (
        <Message
          severity="warn"
          className="w-full"
          text="The fleet is larger than one page, so these totals cover only the top 200 stations. Read the Station list tab for the full figures."
        />
      )}

      {previousMissing && (
        <Message
          severity="info"
          className="w-full"
          text={`No data for ${monthName(summary.previousMonth, "en-US")}: comparisons with it are not available.`}
        />
      )}

      {current?.neverComputed && (
        <Message
          severity="info"
          className="w-full"
          text={`${currentLabel} has not been calculated yet, so the running-month cards are empty. The nightly job fills it in, or run a backfill.`}
        />
      )}

      <div className="grid">
        <div className="col-12 md:col-6 lg:col-4">
          <Metric
            label={`Money taken — ${currentLabel}${soFar}`}
            testId="money-tile"
            value={euro(current?.neverComputed ? null : current?.moneyCents)}
            caption={`${elapsedCaption}${isCurrentMonth && current?.liveDayIncluded ? " · includes today" : ""}`}
          >
            {projected(current?.moneyCents, euro, previous?.moneyCents)}
          </Metric>
        </div>

        <div className="col-12 md:col-6 lg:col-4">
          <Metric
            label={`Money taken — ${previousLabel}`}
            testId="previous-money-tile"
            value={euro(previous?.neverComputed ? null : previous?.moneyCents)}
            caption="Whole month, finished"
          />
        </div>

        <div className="col-12 md:col-6 lg:col-4">
          <Metric
            label="Ahead or behind last month"
            testId="pace-tile"
            value={paceDeltaPercent == null ? "—" : `${paceDeltaPercent >= 0 ? "+" : ""}${paceDeltaPercent.toFixed(1)}%`}
            tone={paceDeltaPercent == null ? "neutral" : paceDeltaPercent >= 0 ? "good" : "bad"}
            caption={
              previousPaceCents == null
                ? tooEarly
                  ? "Needs more of the month to compare"
                  : "Needs both months to compare"
                : isCurrentMonth
                  ? `vs ${euro(previousPaceCents)} — last month at the same point`
                  : `vs ${euro(previousPaceCents)} — ${previousLabel} (whole month)`
            }
          />
        </div>

        <div className="col-12 md:col-6 lg:col-4">
          {/* Both months in one card: the share is only interesting next to its own comparison.
              No "% of money taken": internal stations' revenue is wholly ours and its THG can exceed
              what drivers paid, so cocharge can exceed money taken and the percentage would mislead. */}
          <Metric
            testId="cocharge-share-tile"
            label={TEXTS.shareLabel}
            value={euroCents(current?.neverComputed ? null : current?.cochargeMoneyCents)}
            caption={isCurrentMonth ? `${currentLabel} so far` : "Whole month"}
          >
            {projected(current?.cochargeMoneyCents, euroCents, previous?.cochargeMoneyCents)}
            <div className="text-color-secondary text-sm mt-1">{TEXTS.headlineNote}</div>
            <div className="text-color-secondary text-sm mt-1">{TEXTS.sumLine}</div>
            <div className="mt-3 pt-3 border-top-1 surface-border">
              <div className="flex justify-content-between align-items-baseline">
                <span className="text-color-secondary text-sm">{previousLabel}</span>
                <span className="text-xl font-bold">
                  {euroCents(previous?.neverComputed ? null : previous?.cochargeMoneyCents)}
                </span>
              </div>
            </div>
            <SharePart
              label={cochargeFeeLabel({ cochargeFeePercent: current?.cochargeFeePercent ?? previous?.cochargeFeePercent })}
              explanation={cochargeFeeExplanation(current?.cochargeFeePercent ?? previous?.cochargeFeePercent)}
              current={current?.neverComputed ? null : current?.cochargeFeeCents}
              previous={previous?.neverComputed ? null : previous?.cochargeFeeCents}
              currentLabel={currentLabel}
              previousLabel={previousLabel}
              currentProjection={projected(current?.cochargeFeeCents, euroCents, previous?.cochargeFeeCents)}
            />
            <SharePart
              label={TEXTS.internalRevenueLabel}
              explanation={TEXTS.internalRevenueExplanation}
              current={current?.neverComputed ? null : current?.internalRevenueCents}
              previous={previous?.neverComputed ? null : previous?.internalRevenueCents}
              currentLabel={currentLabel}
              previousLabel={previousLabel}
              currentProjection={projected(current?.internalRevenueCents, euroCents, previous?.internalRevenueCents)}
            />
            <SharePart
              label={TEXTS.thgShareLabel}
              explanation={TEXTS.thgShareExplanation}
              current={current?.neverComputed ? null : current?.thgCochargeCents}
              previous={previous?.neverComputed ? null : previous?.thgCochargeCents}
              currentLabel={currentLabel}
              previousLabel={previousLabel}
              currentProjection={projected(thgPart(current?.thgCochargeCents), euroCents, previousThg(previous?.thgCochargeCents))}
              thg={{
                currentNotApplicable: Boolean(current?.thgNotApplicable),
                previousNotApplicable: Boolean(previous?.thgNotApplicable),
                currentNeverComputed: !current || current.neverComputed,
                previousNeverComputed: !previous || previous.neverComputed,
              }}
            />
            {thgMissingYears.length > 0 && (
              <div className="text-sm mt-3" style={{ color: "var(--orange-700)" }}>
                {TEXTS.missingPricesNote(thgMissingYears)}
              </div>
            )}
          </Metric>
        </div>

        <div className="col-12 md:col-6 lg:col-4">
          <ThgTile
            testId="total-thg-tile"
            label={TEXTS.totalThgLabel}
            explanation={TEXTS.totalThgExplanation}
            current={current}
            previous={previous}
            pick={(m) => m.thgTotalCents}
            currentLabel={currentLabel}
            previousLabel={previousLabel}
            currentCaption={isCurrentMonth ? `${currentLabel} so far` : "Whole month"}
            projection={(v, p) => projected(v, euroCents, p)}
          />
        </div>

        <div className="col-12 md:col-6 lg:col-4">
          <ThgTile
            testId="thg-payout-tile"
            label={TEXTS.thgPayoutLabel}
            explanation={TEXTS.thgPayoutExplanation}
            current={current}
            previous={previous}
            pick={(m) => m.thgPayoutCents}
            currentLabel={currentLabel}
            previousLabel={previousLabel}
            currentCaption={isCurrentMonth ? `${currentLabel} so far` : "Whole month"}
            projection={(v, p) => projected(v, euroCents, p)}
          />
        </div>

        <div className="col-12 md:col-6 lg:col-4">
          <TopStation
            label={`Top station — ${currentLabel}${soFar}`}
            summary={current}
            caption={elapsedCaption}
            onSelectUnit={onSelectUnit}
          />
        </div>

        <div className="col-12 md:col-6 lg:col-4">
          <TopStation
            label={`Top station — ${previousLabel}`}
            summary={previous}
            caption="whole month"
            onSelectUnit={onSelectUnit}
          />
        </div>

        <div className="col-12 md:col-6 lg:col-4">
          {/* Both bases, because the financial model counts connectors while operations count sites.
              Quoting one without the other is where the disagreement CP-388 chased came from. */}
          <Metric
            label={`Money per site — ${previousLabel}`}
            value={
              reconciliation?.perSite?.moneyCents?.value == null
                ? "—"
                : euro(reconciliation.perSite.moneyCents.value)
            }
            caption={
              reconciliation
                ? `${reconciliation.activeSites} active site(s), whole month`
                : "Needs a finished month"
            }
          >
            {reconciliation && (
              <div className="mt-3 pt-3 border-top-1 surface-border">
                <div className="flex justify-content-between align-items-baseline">
                  <span className="text-color-secondary text-sm">Per connector</span>
                  <span className="text-xl font-bold">
                    {reconciliation.perConnector?.moneyCents?.value == null
                      ? "—"
                      : euro(reconciliation.perConnector.moneyCents.value)}
                  </span>
                </div>
                <div className="text-color-secondary text-sm text-right">
                  {reconciliation.activeConnectors} active connector(s)
                  {reconciliation.connectorsPerSite != null &&
                    ` · ×${reconciliation.connectorsPerSite.toFixed(3)}`}
                </div>
              </div>
            )}
          </Metric>
        </div>

        <div className="col-12 md:col-6 lg:col-4">
          <Metric
            label={`Energy delivered — ${previousLabel}`}
            testId="energy-delivered-tile"
            value={
              previous?.neverComputed || !previous
                ? "—"
                : `${Math.round(previous.energyKwh).toLocaleString()} kWh`
            }
            caption={
              previous && !previous.neverComputed && previous.energyKwh > 0
                ? `${Math.round(previous.freeEnergyKwh).toLocaleString()} kWh given away free (${(
                    (previous.freeEnergyKwh / previous.energyKwh) *
                    100
                  ).toFixed(1)}%)`
                : "Whole month"
            }
          />
        </div>
      </div>

      <Card data-testid="compare-card">
        <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <span className="font-bold">Are we making more money?</span>
          <div className="flex gap-2 align-items-center">
            {current?.closure && (
              <Tag value={`${currentLabel}: ${current.closure}`} severity={closureSeverity(current.closure)} />
            )}
            {(previous?.closure ?? previousClosure) && (
              <Tag
                value={`${previousLabel}: ${previous?.closure ?? previousClosure}`}
                severity={closureSeverity((previous?.closure ?? previousClosure) as ClosureState)}
              />
            )}
          </div>
        </div>

        <div className="flex flex-column gap-3">
          <div data-testid="previous-month-row">
            <div className="flex justify-content-between text-sm mb-1 gap-2">
              <span>
                {previousLabel} — whole month
                {samePoint && (
                  <span className="text-color-secondary">
                    {" "}
                    · same point ({samePointLabel}): <span className="font-bold">{euro(previousPaceCents)}</span>
                  </span>
                )}
              </span>
              <span className="font-bold">{euro(previousMoney)}</span>
            </div>
            <div style={{ background: "var(--surface-200)", borderRadius: 4, height: 28, position: "relative" }}>
              {previousMoney != null && (
                <div
                  data-testid="previous-month-bar"
                  style={{
                    width: width(previousMoney),
                    background: "var(--surface-500)",
                    height: "100%",
                    borderRadius: 4,
                  }}
                />
              )}
              {samePoint && (
                // Where last month had got to by the same point. The only fair yardstick for a running month.
                <div
                  data-testid="same-point-marker"
                  title={`${previousLabel} at the same point (${samePointLabel}): ${euro(previousPaceCents)}`}
                  style={{
                    position: "absolute",
                    left: width(previousPaceCents),
                    top: -4,
                    bottom: -4,
                    borderLeft: "2px dashed var(--primary-color)",
                  }}
                />
              )}
            </div>
          </div>

          <div>
            <div className="flex justify-content-between text-sm mb-1">
              <span>
                {currentLabel} — {elapsedCaption}
              </span>
              <span className="font-bold">{euro(currentMoney)}</span>
            </div>
            <div style={{ background: "var(--surface-200)", borderRadius: 4, height: 28 }}>
              {currentMoney != null && (
                <div
                  style={{
                    width: width(currentMoney),
                    background:
                      paceDeltaPercent != null && paceDeltaPercent < 0
                        ? "var(--red-500)"
                        : "var(--green-500)",
                    height: "100%",
                    borderRadius: 4,
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {samePoint && (
          <div className="text-color-secondary text-sm mt-3">
            The dashed line is where {previousLabel} had got to by the same point ({formatDays(daysCovered)} of{" "}
            {daysInCurrentMonth} days). It is a straight-line estimate — only daily session counts are stored,
            not daily money — so treat it as a rough marker, not a measurement. The green or red bar is measured.
          </div>
        )}
      </Card>
    </div>
  );
};

export default PerformanceDashboard;
